'use client';

import { c as _c } from "react/compiler-runtime";
import type { AllCategories, CreditCardOffer } from '@cc-shopping/contracts/compare/schema/types';
import { useConstant } from '@cc-shopping/hooks/useConstant';
import { useStrictContext } from '@cc-shopping/hooks/useStrictContext';
import { BrowserStorageWrapper } from '@cc-shopping/platform-storage';
import { isEqual } from '@cc-shopping/utils/object/isEqual';
import { createContext, useEffect, useState } from 'react';
import { defaultStringArrayParser, useAllEntities } from './hooks/useAllEntities';
import { useCompareDataOrdering } from './hooks/useCompareDataOrdering';
import { useUIUpdates } from './hooks/useUIUpdates';
import type { CompareSessionContextType, CompareSessionProviderProps, MarketplaceIds } from './types';
import { allowedCategories, categoryLabelMapper, getIdsFromQueryParams, LOCAL_STORAGE_KEYS_MAP } from './utils';
const CompareSessionContext = createContext<CompareSessionContextType | null>(null);
export function CompareSessionProvider(t0) {
  const $ = _c(58);
  const {
    arpiOrdering,
    compareData,
    stringArrayParser: t1,
    children
  } = t0;
  const stringArrayParser = t1 === undefined ? defaultStringArrayParser : t1;
  const {
    bestOffer,
    stickyHeaderMinHeights: initialStickyHeaderMinHeights,
    anyLargeOfferTitle: initialAnyLargeOfferTitle,
    anyRatesAndFeesOffer: initialAnyRatesAndFeesOffer
  } = compareData;
  const {
    id: bestOfferId
  } = bestOffer;
  let t2;
  if ($[0] !== bestOfferId) {
    t2 = [bestOfferId, null, null];
    $[0] = bestOfferId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const DEFAULT_IDS_TO_COMPARE = t2;
  const [idsToCompare, setIdsToCompare] = useState(DEFAULT_IDS_TO_COMPARE);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [previouslyViewedIds, setPreviouslyViewedIds] = useState(t3);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  let t4;
  if ($[3] !== bestOffer) {
    t4 = [bestOffer, undefined, undefined];
    $[3] = bestOffer;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [offersToShow, setOffersToShow] = useState(t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [categoriesToShow, setCategoriesToShow] = useState(t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const hasPreselectedIds = useConstant(_temp, t6);
  let t7;
  if ($[7] !== DEFAULT_IDS_TO_COMPARE || $[8] !== compareData.allOffersFromQuery || $[9] !== idsToCompare || $[10] !== stringArrayParser) {
    t7 = {
      idsToCompare,
      setIdsToCompare,
      setPreviouslyViewedIds,
      DEFAULT_IDS_TO_COMPARE,
      offersByDefault: compareData.allOffersFromQuery,
      stringArrayParser
    };
    $[7] = DEFAULT_IDS_TO_COMPARE;
    $[8] = compareData.allOffersFromQuery;
    $[9] = idsToCompare;
    $[10] = stringArrayParser;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    allOffers,
    isLoading
  } = useAllEntities(t7);
  let t8;
  if ($[12] !== allOffers || $[13] !== idsToCompare || $[14] !== initialAnyLargeOfferTitle || $[15] !== initialAnyRatesAndFeesOffer || $[16] !== initialStickyHeaderMinHeights || $[17] !== isLoading || $[18] !== offersToShow) {
    t8 = {
      allOffers,
      isLoading,
      idsToCompare,
      setIdsToCompare,
      offersToShow,
      setOffersToShow,
      initialAnyLargeOfferTitle,
      initialAnyRatesAndFeesOffer,
      initialStickyHeaderMinHeights
    };
    $[12] = allOffers;
    $[13] = idsToCompare;
    $[14] = initialAnyLargeOfferTitle;
    $[15] = initialAnyRatesAndFeesOffer;
    $[16] = initialStickyHeaderMinHeights;
    $[17] = isLoading;
    $[18] = offersToShow;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const {
    areSlotsOpen,
    anyLargeOfferTitle,
    anyRatesAndFeesOffer,
    tallestApplyNow,
    setApplyNowHeightsByIndex,
    calculateMaxApplyNowHeight,
    stickyHeaderMinHeights,
    moveOfferToAvailableSpot
  } = useUIUpdates(t8);
  const transformedCompareData = useCompareDataOrdering(arpiOrdering, compareData);
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = new BrowserStorageWrapper();
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const storage = t9;
  let t10;
  if ($[21] !== allOffers || $[22] !== idsToCompare || $[23] !== offersToShow) {
    t10 = () => {
      const anyChange = !isEqual(offersToShow.map(_temp2), idsToCompare);
      if (anyChange) {
        const allOfferEntities = idsToCompare.map(id => id ? allOffers.find(offer_0 => offer_0.id === id) : undefined);
        setOffersToShow(allOfferEntities);
      }
    };
    $[21] = allOffers;
    $[22] = idsToCompare;
    $[23] = offersToShow;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== allOffers || $[26] !== idsToCompare) {
    t11 = [allOffers, idsToCompare];
    $[25] = allOffers;
    $[26] = idsToCompare;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[28] !== isLoading || $[29] !== offersToShow) {
    t12 = () => {
      if (!isLoading) {
        const eligibleToShow = new Set(allowedCategories);
        const categoriesSet = new Set();
        offersToShow.forEach(offer_1 => {
          offer_1?.compareCategories?.forEach(category => {
            const label = categoryLabelMapper[category];
            if (eligibleToShow.has(category) && label) {
              categoriesSet.add(label);
            }
          });
        });
        setCategoriesToShow(Array.from(categoriesSet));
      }
    };
    t13 = [isLoading, offersToShow];
    $[28] = isLoading;
    $[29] = offersToShow;
    $[30] = t12;
    $[31] = t13;
  } else {
    t12 = $[30];
    t13 = $[31];
  }
  useEffect(t12, t13);
  let t14;
  if ($[32] !== idsToCompare) {
    t14 = id_0 => {
      if (idsToCompare.includes(id_0) || !idsToCompare.includes(null)) {
        return;
      }
      setIdsToCompare(prevIds => {
        const nullIndex = prevIds.indexOf(null);
        if (nullIndex !== -1) {
          setPreviouslyViewedIds(prevIds_0 => {
            const newIds = [id_0, ...prevIds_0.filter(prevId => prevId !== id_0)].slice(0, 10);
            storage.setItem(LOCAL_STORAGE_KEYS_MAP.PREVIOUS_IDS, JSON.stringify(newIds));
            return newIds;
          });
          const newIds_0 = [...prevIds];
          newIds_0[nullIndex] = id_0;
          storage.setItem(LOCAL_STORAGE_KEYS_MAP.IDS_TO_COMPARE, JSON.stringify(newIds_0));
          return newIds_0;
        }
        return prevIds;
      });
    };
    $[32] = idsToCompare;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const addIdToCompare = t14;
  let t15;
  if ($[34] !== moveOfferToAvailableSpot) {
    t15 = id_1 => {
      setIdsToCompare(prevIds_1 => {
        const newIds_1 = prevIds_1.map(prevId_0 => prevId_0 === id_1 ? null : prevId_0);
        moveOfferToAvailableSpot(newIds_1);
        storage.setItem(LOCAL_STORAGE_KEYS_MAP.IDS_TO_COMPARE, JSON.stringify(newIds_1));
        return newIds_1;
      });
    };
    $[34] = moveOfferToAvailableSpot;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const removeIdToCompare = t15;
  let t16;
  if ($[36] !== addIdToCompare || $[37] !== allOffers || $[38] !== anyLargeOfferTitle || $[39] !== anyRatesAndFeesOffer || $[40] !== areSlotsOpen || $[41] !== calculateMaxApplyNowHeight || $[42] !== categoriesToShow || $[43] !== hasPreselectedIds || $[44] !== idsToCompare || $[45] !== isLoading || $[46] !== offersToShow || $[47] !== previouslyViewedIds || $[48] !== removeIdToCompare || $[49] !== setApplyNowHeightsByIndex || $[50] !== showStickyHeader || $[51] !== stickyHeaderMinHeights || $[52] !== tallestApplyNow || $[53] !== transformedCompareData) {
    t16 = {
      compareData: transformedCompareData,
      idsToCompare,
      previouslyViewedIds,
      areSlotsOpen,
      addIdToCompare,
      removeIdToCompare,
      isLoading,
      offersToShow,
      categoriesToShow,
      showStickyHeader,
      setShowStickyHeader,
      anyLargeOfferTitle,
      anyRatesAndFeesOffer,
      tallestApplyNow,
      setApplyNowHeightsByIndex,
      stickyHeaderMinHeights,
      calculateMaxApplyNowHeight,
      allOffers,
      hasPreselectedIds
    };
    $[36] = addIdToCompare;
    $[37] = allOffers;
    $[38] = anyLargeOfferTitle;
    $[39] = anyRatesAndFeesOffer;
    $[40] = areSlotsOpen;
    $[41] = calculateMaxApplyNowHeight;
    $[42] = categoriesToShow;
    $[43] = hasPreselectedIds;
    $[44] = idsToCompare;
    $[45] = isLoading;
    $[46] = offersToShow;
    $[47] = previouslyViewedIds;
    $[48] = removeIdToCompare;
    $[49] = setApplyNowHeightsByIndex;
    $[50] = showStickyHeader;
    $[51] = stickyHeaderMinHeights;
    $[52] = tallestApplyNow;
    $[53] = transformedCompareData;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const contextValue = t16;
  let t17;
  if ($[55] !== children || $[56] !== contextValue) {
    t17 = <CompareSessionContext.Provider value={contextValue}>{children}</CompareSessionContext.Provider>;
    $[55] = children;
    $[56] = contextValue;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  return t17;
}
function _temp2(offer) {
  return offer ? offer.id : null;
}
function _temp() {
  return !!getIdsFromQueryParams();
}
export const useCompareContext = () => {
  const compareData = useStrictContext(CompareSessionContext);
  return compareData;
};